import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {useMemo, useRef, useState} from 'react';
import ArrSectionCard from '../../../../common/arr-accordion-v2/ArrSectionCard';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import {
  IssueCodeDefinition,
  IssueCoding,
  TopLevelIssueCategory,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {ISelectItem} from '../../../../../shared/types';
import {issueCodesViewMockData} from './issueCodesViewMockData';
import {
  ALL_CATEGORIES_ID,
  matchesIssueCodeCategory,
  matchesIssueCodeViewSearch,
  resolveCategoryLabel,
} from './utils';
import BackToTopButton from '../back-to-top/BackToTopButton';
import './IssueCodesTabView.scss';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';

export interface IssueCodesTabViewProps {
  data?: IssueCoding;
}

const ISSUE_CODES_TOOLTIP_KEY_PREFIX =
  'ai.review.protocolWizard.issueCodes.tooltips';

const FieldBlock = ({
  label,
  value,
  dataTestId,
}: {
  label: string;
  value: string;
  dataTestId?: string;
}) => (
  <Box className="issue-codes-tab-view__field" data-testid={dataTestId}>
    <Box component="span" className="issue-codes-tab-view__field-label">
      {label}
    </Box>
    <Typography className="issue-codes-tab-view__field-value typo-text body2_size">
      {value}
    </Typography>
  </Box>
);

const BulletList = ({
  label,
  items,
  dataTestId,
}: {
  label: string;
  items: string[];
  dataTestId: string;
}) => (
  <Box className="issue-codes-tab-view__bullet-group" data-testid={dataTestId}>
    <Box component="span" className="issue-codes-tab-view__field-label">
      {label}
    </Box>
    <ul className="issue-codes-tab-view__bullet-list">
      {items.filter(Boolean).map((item, index) => (
        <li key={`${dataTestId}-${index}`}>{item}</li>
      ))}
    </ul>
  </Box>
);

const CategoryViewItem = ({
  category,
  index,
}: {
  category: TopLevelIssueCategory;
  index: number;
}) => (
  <Box
    className="issue-codes-tab-view__category-item"
    data-testid={`issue-codes-view-category-${index}`}
  >
    {category.category_id && (
      <Box
        component="span"
        className="issue-codes-tab-view__category-chip"
      >
        {category.category_id}
      </Box>
    )}
    <Box className="issue-codes-tab-view__category-text">
      <Typography className="issue-codes-tab-view__category-title typo-text subhead5_size">
        {category.category_label}
      </Typography>
      <Typography className="issue-codes-tab-view__category-description typo-text body4_size">
        {category.category_description}
      </Typography>
    </Box>
  </Box>
);

const IssueCodeViewBody = ({
  code,
  index,
  categoryLabel,
}: {
  code: IssueCodeDefinition;
  index: number;
  categoryLabel: string;
}) => (
  <Box
    className="issue-codes-tab-view__code-body"
    data-testid={`issue-codes-view-code-${index}`}
  >
    <Box className="issue-codes-tab-view__meta-row">
      <FieldBlock
        label={t('ai.review.protocolWizard.issueCodes.fields.category')}
        value={categoryLabel}
        dataTestId={`issue-codes-view-category-field-${index}`}
      />
      <FieldBlock
        label={t('ai.review.protocolWizard.issueCodes.fields.priorityLabel')}
        value={code.priority}
        dataTestId={`issue-codes-view-priority-field-${index}`}
      />
    </Box>
    <FieldBlock
      label={t('ai.review.protocolWizard.issueCodes.fields.definition')}
      value={code.definition}
      dataTestId={`issue-codes-view-definition-field-${index}`}
    />
    <Box className="issue-codes-tab-view__lists-grid">
      <BulletList
        label={t(
          'ai.review.protocolWizard.issueCodes.fields.includeIfAnyApply'
        )}
        items={code.include_if_any_apply}
        dataTestId={`issue-codes-view-include-${index}`}
      />
      <BulletList
        label={t(
          'ai.review.protocolWizard.issueCodes.fields.responsiveExamples'
        )}
        items={code.responsive_examples}
        dataTestId={`issue-codes-view-responsive-${index}`}
      />
      <BulletList
        label={t(
          'ai.review.protocolWizard.issueCodes.fields.excludeIfAnyApply'
        )}
        items={code.exclude_if_any_apply}
        dataTestId={`issue-codes-view-exclude-${index}`}
      />
      <BulletList
        label={t(
          'ai.review.protocolWizard.issueCodes.fields.nonResponsiveExamples'
        )}
        items={code.non_responsive_examples}
        dataTestId={`issue-codes-view-non-responsive-${index}`}
      />
    </Box>
  </Box>
);

const NoneContent = ({dataTestId}: {dataTestId: string}) => (
  <Typography
    component="p"
    align="left"
    className="issue-codes-tab-view__none typo-text body3_size"
    data-testid={dataTestId}
  >
    {t('questionReadOnlyList.none')}
  </Typography>
);

const IssueCodesTabView: React.FC<IssueCodesTabViewProps> = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ISelectItem | null>(
    null
  );
  const data = useSelector(
    (state: RootState) => state.aiReviewV2.protocol.issueCoding
  );
  const categories = data.top_level_issue_categories;
  const hasCategories = categories.length > 0;

  const categoryOptions: ISelectItem[] = useMemo(() => {
    const options = categories
      .filter((category) => category.category_id.trim())
      .map((category) => ({
        id: category.category_id,
        name: category.category_label || category.category_id,
      }));

    return [
      {
        id: ALL_CATEGORIES_ID,
        name: t('ai.review.protocolWizard.issueCodes.fields.allCategories'),
      },
      ...options,
    ];
  }, [categories]);

  const filteredIssueCodes = useMemo(() => {
    const categoryId =
      !selectedCategory || selectedCategory.id === ALL_CATEGORIES_ID
        ? null
        : String(selectedCategory.id);

    return data.issue_codes.filter((code) => {
      const categoryLabel = resolveCategoryLabel(
        data.top_level_issue_categories,
        code.category
      );
      return (
        matchesIssueCodeViewSearch(code, searchTerm, categoryLabel) &&
        matchesIssueCodeCategory(code, categoryId)
      );
    });
  }, [
    data.issue_codes,
    data.top_level_issue_categories,
    searchTerm,
    selectedCategory,
  ]);

  const selectedCategoryValue =
    selectedCategory ??
    categoryOptions.find((option) => option.id === ALL_CATEGORIES_ID) ??
    null;

  const renderStringListSection = (
    labelKey: string,
    items: string[],
    dataTestId: string,
    tooltipKey: string,
    defaultExpanded = true
  ) => {
    const nonEmptyItems = items.filter(Boolean);
    const isEmpty = nonEmptyItems.length === 0;

    return (
      <ArrSectionCard
        accordion
        defaultExpanded={defaultExpanded}
        showInfoIcon
        infoContent={t(tooltipKey)}
        label={t(labelKey)}
        items={isEmpty ? [] : [nonEmptyItems]}
        showAddButton={false}
        showDeleteButton={false}
        bodyVariant="transparent"
        className="issue-codes-tab-view__plain-section"
        dataTestId={dataTestId}
        beforeBody={
          isEmpty ? (
            <NoneContent dataTestId={`${dataTestId}-none`} />
          ) : undefined
        }
        renderItem={(list: string[]) => (
          <ul
            className="issue-codes-tab-view__section-list"
            data-testid={`${dataTestId}-list`}
          >
            {list.map((item, index) => (
              <li key={`${dataTestId}-item-${index}`}>{item}</li>
            ))}
          </ul>
        )}
      />
    );
  };

  return (
    <Box
      ref={scrollContainerRef}
      className="issue-codes-tab-view"
      data-testid="issue-codes-tab-view"
    >
      <ArrSectionCard
        accordion
        defaultExpanded
        showInfoIcon
        infoContent={t(`${ISSUE_CODES_TOOLTIP_KEY_PREFIX}.topLevelCategories`)}
        bodyVariant="transparent"
        label={t(
          'ai.review.protocolWizard.issueCodes.sections.topLevelCategories'
        )}
        items={hasCategories ? categories : []}
        showAddButton={false}
        showDeleteButton={false}
        className="issue-codes-tab-view__categories-section"
        dataTestId="issue-codes-view-categories"
        beforeBody={
          hasCategories ? undefined : (
            <NoneContent dataTestId="issue-codes-view-categories-none" />
          )
        }
        renderItem={(category: TopLevelIssueCategory, index: number) => (
          <CategoryViewItem category={category} index={index} />
        )}
      />

      <ArrSectionCard
        accordion
        defaultExpanded
        showInfoIcon
        infoContent={t(`${ISSUE_CODES_TOOLTIP_KEY_PREFIX}.issueCodes`)}
        itemAccordion
        bodyVariant="transparent"
        label={t('ai.review.protocolWizard.issueCodes.sections.issueCodes')}
        items={filteredIssueCodes}
        showAddButton={false}
        showDeleteButton={false}
        className="issue-codes-tab-view__issue-codes-section"
        dataTestId="issue-codes-view-issue-codes"
        beforeBody={
          <>
            <Box className="issue-codes-tab-view__filters">
              <Box className="issue-codes-tab-view__search">
                <ArrTextField
                  type="search"
                  sizeVariant="medium"
                  placeholder={t(
                    'ai.review.protocolWizard.issueCodes.fields.searchIssueCodes'
                  )}
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onClear={() => setSearchTerm('')}
                  dataTestId="issue-codes-view-search"
                />
              </Box>
              <Box className="issue-codes-tab-view__category-filter">
                <ArrSingleSelectDropdown
                  dropdownLabel={t(
                    'ai.review.protocolWizard.issueCodes.fields.category'
                  )}
                  dropdownItems={categoryOptions}
                  selectedValues={selectedCategoryValue}
                  onSelectionChange={(value) => setSelectedCategory(value)}
                  sizeVariant="medium"
                  dataTestId="issue-codes-view-category-filter"
                />
              </Box>
            </Box>
            {filteredIssueCodes.length === 0 &&
              (Boolean(searchTerm.trim()) ||
                Boolean(
                  selectedCategory && selectedCategory.id !== ALL_CATEGORIES_ID
                )) && (
                <Box
                  className="issue-codes-tab-view__no-results"
                  data-testid="issue-codes-view-no-results"
                >
                  {t(
                    'ai.review.protocolWizard.issueCodes.fields.noIssueCodesFound'
                  )}
                </Box>
              )}
          </>
        }
        renderItemHeader={(code: IssueCodeDefinition) => (
          <Box className="issue-codes-tab-view__code-title-row">
            <Box component="span" className="issue-codes-tab-view__code-chip">
              {code.code_id}
            </Box>
            <Typography className="issue-codes-tab-view__code-name typo-text subhead4_size">
              {code.code_name}
            </Typography>
          </Box>
        )}
        renderItem={(code: IssueCodeDefinition, index: number) => (
          <IssueCodeViewBody
            code={code}
            index={index}
            categoryLabel={resolveCategoryLabel(
              data.top_level_issue_categories,
              code.category
            )}
          />
        )}
      />

      {renderStringListSection(
        'ai.review.protocolWizard.issueCodes.sections.crossIssueOverlapRules',
        data.cross_issue_overlap_rules,
        'issue-codes-view-overlap-rules',
        `${ISSUE_CODES_TOOLTIP_KEY_PREFIX}.crossIssueOverlapRules`
      )}

      {renderStringListSection(
        'ai.review.protocolWizard.issueCodes.sections.edgeCaseHandling',
        data.edge_case_handling,
        'issue-codes-view-edge-cases',
        `${ISSUE_CODES_TOOLTIP_KEY_PREFIX}.edgeCaseHandling`
      )}
      <BackToTopButton scrollContainerRef={scrollContainerRef} />
    </Box>
  );
};

export default IssueCodesTabView;
